import { useLanguageStore } from 'store'
import type { App } from 'vue'
import { GeetestCaptcha, type CaptchaConfig } from 'vue3-geetest'
import { Geetest } from 'vue3-geetest'

export type GT4VerifyParams = {
  captchaId?: string
}

export type GeeTestResult = {
  captcha_id: string
  captcha_output: string
  gen_time: string
  lot_number: string
  pass_token: string
}

export type GeeTestInstance = {
  onReady: () => GeeTestInstance
  showCaptcha: () => GeeTestInstance
  onSuccess: (cb: () => void) => GeeTestInstance
  getValidate: () => GeeTestResult
  onClose: (cb: () => void) => GeeTestInstance
  reset: () => GeeTestInstance
  destroy?: () => void
}

export type GT4Options = {
  captchaId: string
}

/**
 * 项目的多语言：zh_CN, en, zh_HK, ar, ja, ko, fr, es, ru, de, pt, hi, vi, th, tr, it, id
 *
 * 极验的多语言 language?: 'zho' | 'eng' | 'zho-tw' | 'zho-hk' | 'udm' | 'jpn' | 'ind' | 'kor' | 'rus' | 'ara' | 'spa' | 'pon' | 'por' | 'fra' | 'deu'
 *
 * zho 是中文
 */

const geeTestLangByProjectLang = (lang: string) => {
  switch (lang) {
    case 'zh_CN':
      return 'zho'
    case 'en':
      return 'eng'
    case 'zh_HK':
      return 'zho-hk'
    case 'ar':
      return 'ara'
    case 'ja':
      return 'jpn'
    case 'ko':
      return 'kor'
    case 'fr':
      return 'fra'
    case 'es':
      return 'spa'
    case 'ru':
      return 'rus'
    case 'de':
      return 'deu'
    case 'pt':
      return 'por'
    case 'id':
      return 'ind'
    default:
      return 'eng'
  }
}

export class GT4 {
  static app: App<Element> | null = null
  static options: Partial<GT4Options> = {
    captchaId: '',
  }
  static oldLanguage = 'zho'
  static instanceMap = new Map<string, GeeTestInstance>()
  static geetestApp: App<Element> | null = null
  static mountEl: HTMLElement | null = null

  /** 返回 null 表示用户关闭了验证 */
  static async verify(params: GT4VerifyParams): Promise<GeeTestResult | null> {
    let captchaId = params.captchaId
    if (!captchaId) captchaId = GT4.options.captchaId
    if (!captchaId) return Promise.reject(new Error('获取配置失败'))
    const languageStore = useLanguageStore()
    if (!GT4.mountEl) {
      GT4.mountEl = document.createElement('div')
    }

    return new Promise((resolve) => {
      const lang = geeTestLangByProjectLang(languageStore.currentLanguage)
      const config: CaptchaConfig = {
        captchaId: captchaId,
        product: 'bind',
        nativeButton: {
          width: '400px',
          height: '48px',
        },
        language: lang,
      }

      if (GT4.instanceMap.get(captchaId)) {
        if (this.oldLanguage !== lang) {
          GT4.instanceMap.get(captchaId)!.destroy?.()
          GT4.instanceMap.delete(captchaId)
          GT4.geetestApp?.unmount()
          if (GT4.mountEl) {
            document.body.removeChild(GT4.mountEl)
            GT4.mountEl = document.createElement('div')
          }
        } else {
          GT4.instanceMap.get(captchaId)!.showCaptcha()
          GT4.instanceMap.get(captchaId)!.onSuccess(() => {
            resolve(GT4.instanceMap.get(captchaId)!.getValidate())
          })
          GT4.instanceMap.get(captchaId)!.onClose(() => {
            resolve(null)
          })
          return
        }
      }
      this.oldLanguage = lang
      GT4.geetestApp = createApp(GeetestCaptcha, {
        config,
        onInitialized: (_instance: GeeTestInstance) => {
          GT4.instanceMap.set(captchaId, _instance)
          _instance.showCaptcha()
          _instance.onSuccess(() => {
            resolve(_instance!.getValidate())
          })

          GT4.instanceMap.get(captchaId)!.onClose(() => {
            resolve(null)
          })
        },
      })

      if (this.app) {
        GT4.geetestApp._context = this.app._context
      }

      GT4.geetestApp.use(Geetest, config)

      GT4.geetestApp.mount(GT4.mountEl!)
      document.body.appendChild(GT4.mountEl!)
    })
  }

  static install(app: App<Element>, options?: GT4Options) {
    this.app = app
    GT4.options = {
      ...GT4.options,
      ...options,
    }
  }
}
